import {
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  ButtonGroup,
  Table,
  Tbody,
  Thead,
  Td,
  Tr,
  Th,
  Stack,
  Radio,
  RadioGroup,
  Icon,
  Heading,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
} from "@chakra-ui/react";
import { api } from "../../../../utilities/apiRequest";
import { AdminPortalAccountLicenseLine } from "../../../../shared/_core/admin.types";
import { useState } from "react";
import { RiCheckDoubleLine } from "react-icons/ri";
import { FormLabel } from "react-bootstrap";

type Props = {
  accountLicenses: AdminPortalAccountLicenseLine[];
  onClose: () => void;
  onConfirm: (licenseCode: string, quantity: number) => void;
};

export function ServiceAccountCreateLicenseModalContent({
  accountLicenses,
  onClose,
  onConfirm,
}: Props) {
  const licensesQuery = api.admin.license.list.useQuery({});
  const [selectedLicense, setSelectedLicense] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const allLicenses = licensesQuery.data?.licenses || [];
  allLicenses.sort((a, b) => a.licenseCode.localeCompare(b.licenseCode));
  const existingLicenses = allLicenses.filter((license) =>
    accountLicenses.some((line) => line.detail.code === license.licenseCode),
  );
  const availableLicenses = allLicenses.filter(
    (license) =>
      !existingLicenses.some(
        (line) => line.licenseCode === license.licenseCode,
      ),
  );

  const handleClickAddLicense = () => {
    if (
      selectedLicense &&
      quantity > 0 &&
      !accountLicenses.some((l) => l.licenseLineID === selectedLicense)
    ) {
      onConfirm(selectedLicense, quantity);
    }
    onClose();
  };

  return (
    <chakra-scope>
      <ModalContent>
        <ModalHeader>Add license to account</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack h="full" spacing={4}>
            <Table variant="data-table">
              <Thead>
                <Tr>
                  <Th>Select</Th>
                  <Th>Code</Th>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th>Display name</Th>
                  <Th>Type</Th>
                  <Th>Price (mo/yr)</Th>
                  <Th>Users granted</Th>
                </Tr>
              </Thead>
              <Tbody>
                {availableLicenses.map((license) => (
                  <Tr
                    key={license.licenseCode}
                    onClick={() => setSelectedLicense(license.licenseCode)}>
                    <Td>
                      <Radio
                        value={license.licenseCode}
                        isChecked={selectedLicense === license.licenseCode}
                      />
                    </Td>
                    <Td>{license.licenseCode}</Td>
                    <Td>{license.internalName}</Td>
                    <Td>{license.internalDescription}</Td>
                    <Td>{license.displayName}</Td>
                    <Td>{license.type}</Td>
                    <Td>
                      {license.price.mo} / {license.price.yr}
                    </Td>
                    <Td>{license.entitlements.users}</Td>
                  </Tr>
                ))}
                <Tr>
                  <Td colSpan={8}>
                    <Heading
                      color="brand.700"
                      fontStyle="italic"
                      size="sm"
                      as="span">
                      Existing licenses (already on account)
                    </Heading>
                  </Td>
                </Tr>
                {existingLicenses.map((license) => (
                  <Tr key={license.licenseCode}>
                    <Td>
                      <Icon as={RiCheckDoubleLine} />
                    </Td>
                    <Td>{license.licenseCode}</Td>
                    <Td>{license.internalName}</Td>
                    <Td>{license.internalDescription}</Td>
                    <Td>{license.displayName}</Td>
                    <Td>{license.type}</Td>
                    <Td>
                      {license.price.mo} / {license.price.yr}
                    </Td>
                    <Td>{license.entitlements.users}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Stack>
        </ModalBody>
        <ModalFooter flexDir="column" alignItems="end" gap="4">
          <FormControl w="fit-content">
            <FormLabel>Quantity</FormLabel>
            <NumberInput
              maxW="30rem"
              name="quantity"
              value={quantity}
              onChange={(e) => setQuantity(Number.isNaN(+e) ? 0 : +e)}>
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
          <ButtonGroup>
            <Button onClick={onClose} colorScheme="gray">
              Cancel
            </Button>
            <Button
              onClick={handleClickAddLicense}
              isDisabled={licensesQuery.isLoading || selectedLicense === null}>
              Add license
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </chakra-scope>
  );
}
